// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Typography,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import SuccessPopup from "./SuccessPopup";

import EditIcon from "../../../assets/svg/EditIcon.svg";
const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const BasalDosePopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const { showAlert } = useAlert();
  const [comment, setComment] = useState("");
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [chemicalOptions, setChemicalOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({
    selectedDate: "",
    formDataSet: [],
  });

  let farmerPlotId = getFarmerPlotId();

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const scrollableContainerStyle = {
    maxHeight: "300px", // Adjust the height as needed
    overflowY: "auto",
  };
  const [formDataSet, setFormDataSet] = useState([
    {
      Fertilizer: "",
      unit: "",
      dosePerAcre: "",
    },
  ]);
  useEffect(() => {
    const fetchChemicalOptions = async () => {
      try {
        const chemicalResponse = await instance.get(
          "plotengagement/get-fertilizer-names?stage=BASAL_DOSE"
        );
        const chemicalOptions = chemicalResponse.data.map(
          (chemical) => chemical.name
        );

        const unitResponse = await instance.get(
          "plotengagement/get-units?stage=BASAL_DOSE"
        );
        const unitOptions = unitResponse.data.map((unit) => unit.name);
        setChemicalOptions(chemicalOptions);
        setUnitOptions(unitOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchChemicalOptions();
  }, []);
  const rows = [
    { label: t("Date of Activity"), type: "datePicker" },
    {
      label: t("Fertilizer"),
      type: "dropdown",
      options: chemicalOptions,
      key: "Fertilizer",
    },
    { label: t("Unit"), type: "dropdown", options: unitOptions, key: "unit" },
    { label: t("Dose per acre"), type: "textfield", key: "dosePerAcre" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-basal-dose?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  const handleEditClick = async (basalDoseId) => {
    try {
      const response = await instance.get(
        `plotengagement/get-basal-dose-by-id?farmer_plot=${farmerPlotId}&basal_dose_id=${basalDoseId}`
      );
      const specificData = response.data;
      const updatedFormData = specificData.map((data) => ({
        selectedDate: new Date(data.date_of_activity),
        Fertilizer: data.fertilizer,
        unit: data.unit,
        dosePerAcre: data.dose_per_acre,
        id: data.id,
      }));
      setFormDataSet(updatedFormData);
      setComment(specificData[0].comments);
      setIsEditMode(true);
    } catch (error) {
      console.error("Error:", error);
      showAlert("error", "Error fetching specific data");
    }
  };

  const handleChange = (field, value, index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: value,
      };
      return newData;
    });

    if (field === "selectedDate") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        selectedDate: "",
      }));
    }
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (field === "Fertilizer") {
        if (updatedErrors.formDataSet[index]) {
          updatedErrors.formDataSet[index].chemicalName = "";
        }
      } else if (field === "unit") {
        if (updatedErrors.formDataSet[index]) {
          updatedErrors.formDataSet[index].unit = "";
        }
      } else if (field === "dosePerAcre") {
        if (updatedErrors.formDataSet[index]) {
          updatedErrors.formDataSet[index].dosePerAcre = "";
        }
      }
      return updatedErrors;
    });
  };
  const groupedData = {};
  if (existingData) {
    existingData.forEach((data) => {
      if (!groupedData[data.date_of_activity]) {
        groupedData[data.date_of_activity] = [data];
      } else {
        groupedData[data.date_of_activity].push(data);
      }
    });
  }
  const handleAddMore = () => {
    const newFormData = {
      Fertilizer: "",
      unit: "",
      dosePerAcre: "",
    };
    setFormDataSet((prevData) => [...prevData, newFormData]);
  };
  const handleDeleteForm = (index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 1);
      return newData;
    });
  };

  const validateFields = () => {
    let formValid = true;
    let updatedErrors = { selectedDate: "", formDataSet: [] };

    if (!formDataSet.some((formData) => formData.selectedDate)) {
      updatedErrors = {
        ...updatedErrors,
        selectedDate: "Date of Activity is required",
      };
      formValid = false;
    } else {
      updatedErrors = {
        ...updatedErrors,
        selectedDate: "", // Clear the error for selectedDate if it's selected
      };
    }

    const fieldErrors = formDataSet.map((formData) => {
      let fieldError = {};

      if (!formData.Fertilizer) {
        fieldError = {
          ...fieldError,
          chemicalName: "Please select a Fertilizer",
        };
        formValid = false;
      }

      if (!formData.unit) {
        fieldError = {
          ...fieldError,
          unit: "Please select a unit",
        };
        formValid = false;
      }

      if (!formData.dosePerAcre) {
        fieldError = {
          ...fieldError,
          dosePerAcre: "Dose per acre is required",
        };
        formValid = false;
      }

      return fieldError;
    });

    updatedErrors = {
      ...updatedErrors,
      formDataSet: fieldErrors,
    };

    setErrors(updatedErrors);

    return formValid;
  };

  const handleSubmit = () => {
    const isValid = validateFields();
    if (isValid) {
      let submissionData = {};
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };

      if (!isEditMode) {
        const formattedFormData = formDataSet.map((formData) => ({
          date_of_activity: formData.selectedDate
            ? formatDate(formData.selectedDate)
            : null,
          fertilizer: formData["Fertilizer"],
          unit: formData["unit"],
          dose_per_acre: parseFloat(formData["dosePerAcre"]),
          farmer_plot: farmerPlotId,
          comments: comment,
        }));
        submissionData = formattedFormData;
      } else {
        const formData = formDataSet[0];
        submissionData = {
          id: formData.id,
          date_of_activity: formData.selectedDate
            ? formatDate(formData.selectedDate)
            : null,
          fertilizer: formData["Fertilizer"],
          unit: formData["unit"],
          dose_per_acre: parseFloat(formData["dosePerAcre"]),
          farmer_plot: farmerPlotId,
          comments: comment,
        };
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-basal-dose-by-id"
        : "plotengagement/create-basal-dose";
      instance
        .post(apiUrl, submissionData)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.err("error creating/updating basal-dose", err);
          showAlert("error", "Some Error Occured");
        });
    }
  };
  const handleClose = () => {
    setOpen(false);
    onClose();
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Basal Dose")}
              </DialogTitle>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          <div style={{ ...scrollableContainerStyle }}>
            {!isEditMode ? (
              <div style={{ ...scrollableContainerStyle }}>
                {existingData &&
                  existingData.map((data, index) => (
                    <Card key={index} style={{ marginBottom: "10px" }}>
                      <CardContent>
                        <Grid container style={{ marginTop: "1px" }}>
                          <Grid item xs={5}>
                            <InputLabel className={classes.taxtLabel}>
                              {t("Date of Activity")}
                            </InputLabel>
                          </Grid>
                          <Grid item xs={2} style={{ marginTop: "10px" }}>
                            <img
                              src={EditIcon}
                              onClick={() => handleEditClick(data.id)}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Typography style={{ marginTop: "10px" }}>
                              {data.date_of_activity}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Divider style={{ margin: "10px 0" }} />
                        <Grid container style={{ marginTop: "10px" }}>
                          <Grid item xs={4}>
                            <InputLabel className={classes.taxtLabel}>
                              {t("Fertilizer")}
                            </InputLabel>
                          </Grid>
                          <Grid item xs={4}>
                            <InputLabel className={classes.taxtLabel}>
                              {t("Unit")}
                            </InputLabel>
                          </Grid>
                          <Grid item xs={4}>
                            <InputLabel className={classes.taxtLabel}>
                              {t("Dose per Acre")}
                            </InputLabel>
                          </Grid>
                          <Grid container>
                            <Grid item xs={4}>
                              <Typography>{data.fertilizer}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography>{data.unit}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography>{data.dose_per_acre}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <div style={{ marginTop: "20px" }}>
                          <Typography
                            style={{ fontWeight: 700, fontSize: "13px" }}
                          >
                            {t("Comment")}:
                            <span
                              style={{
                                fontWeight: 500,
                                fontSize: "12px",
                                marginLeft: "10px",
                              }}
                            >
                              {data.comments}
                            </span>
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : null}
          </div>

          <div>
            <Typography className={classes.taxtTitle}>
              {t("Fertilizer with Dose")}
            </Typography>
          </div>
          <div style={{ ...scrollableContainerStyle }}>
            {formDataSet.map((formData, formIndex) => (
              <div key={formIndex} style={{ padding: "0px" }}>
                {rows.map((row, index) => (
                  <Grid key={index} container style={{ marginTop: "10px" }}>
                    <Grid item xs={7}>
                      <InputLabel className={classes.taxtLabel}>
                        {t(row.label)}
                      </InputLabel>
                    </Grid>
                    <Grid item xs={5}>
                      {row.type === "datePicker" ? (
                        /* Render date picker component */
                        <>
                          <TextField
                            fullWidth
                            placeholder="DD/MM/YYYY"
                            value={
                              formData["selectedDate"]
                                ? formData["selectedDate"].toLocaleDateString()
                                : ""
                            }
                            error={errors.selectedDate !== ""}
                            helperText={errors.selectedDate}
                            InputProps={{
                              sx: {
                                height: "40px",
                                borderRadius: "5px", // You can adjust this if needed

                                background: "#FFF",
                                border: "1px solid #C7BDBD",
                              },
                              readOnly: true,
                              onClick: () =>
                                document
                                  .getElementById(`date-picker-${formIndex}`)
                                  .click(),
                            }}
                          />
                          <DatePicker
                            id={`date-picker-${formIndex}`}
                            dateFormat="dd/MM/yyyy"
                            selected={formData["selectedDate"]}
                            onChange={(date) =>
                              handleChange("selectedDate", date, formIndex)
                            }
                            className={classes.datepicker}
                          />
                        </>
                      ) : row.type === "dropdown" ? (
                        <>
                          <Select
                            value={formData[row.key]}
                            error={
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex].chemicalName
                                ? true
                                : false
                            }
                            helperText={
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex].chemicalName
                            }
                            onChange={(event) =>
                              handleChange(
                                row.key,
                                event.target.value,
                                formIndex
                              )
                            }
                            style={{ width: "100%" }}
                            sx={{
                              height: "40px", // Set the desired height
                              border: "1px solid #9C9FA0",
                              borderRadius: "5px",
                            }}
                          >
                            {row.options.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.formDataSet[formIndex] && (
                            <div>
                              {row.key === "Fertilizer" &&
                                errors.formDataSet[formIndex].chemicalName && (
                                  <Typography variant="caption" color="error">
                                    {errors.formDataSet[formIndex].chemicalName}
                                  </Typography>
                                )}
                              {row.key === "unit" &&
                                errors.formDataSet[formIndex].unit && (
                                  <Typography variant="caption" color="error">
                                    {errors.formDataSet[formIndex].unit}
                                  </Typography>
                                )}
                            </div>
                          )}
                        </>
                      ) : (
                        <TextField
                          type="number"
                          value={formData[row.key]}
                          onChange={(event) =>
                            handleChange(row.key, event.target.value, formIndex)
                          }
                          helperText={
                            errors.formDataSet[formIndex] &&
                            errors.formDataSet[formIndex][row.key]
                          }
                          error={
                            errors.formDataSet[formIndex] &&
                            errors.formDataSet[formIndex][row.key]
                              ? true
                              : false
                          }
                          style={{ width: "100%" }}
                          size="small"
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px", // You can adjust this if needed
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                ))}

                {formIndex < formDataSet.length - 1 && (
                  <div>
                    <Divider style={{ margin: "10px 0" }} />
                    <Grid container>
                      <Grid item xs={10}></Grid>
                      <Grid item xs={2}>
                        <IconButton
                          aria-label="delete"
                          onClick={() =>
                            handleDeleteForm(
                              !isEditMode ? formIndex + 1 : formIndex + 1
                            )
                          }
                        >
                          <CloseIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogActions style={{ display: "grid" }}>
          {!isEditMode ? (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "10px",
                marginRight: "16px",
                width: "140px",
              }}
            >
              <Button
                onClick={handleAddMore}
                style={{
                  background: "#6FA34E",
                  border: "#538434",
                  borderRadius: "10px",
                  color: "#FFF",
                  textTransform: "none",
                }}
              >
                <AddIcon style={{ marginRight: "5px", fontSize: "15px" }} />{" "}
                {t("Add More")}
              </Button>
            </div>
          ) : null}
        </DialogActions>
        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment (Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
          />
        </div>

        <div
          style={{
            display: "flex",
            margin: "auto",
            justifyContent: "center",
            width: "140px",
          }}
        >
          <CustomButton onClick={handleSubmit} hideIcon>
            {t("Submit")}
          </CustomButton>
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default BasalDosePopup;
